import React, { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react'
import { ApiConfig, APP_ENV, AppEnv, apiConfigStrategy, isApiConfigEnabled } from './index'

interface ApiConfigContextValue {
  config: ApiConfig
  updateConfig: (config: Partial<ApiConfig>) => void
  resetConfig: () => void
  isEnabled: boolean
  appEnv: AppEnv
  currentUrls: { backend: string }
}

const ApiConfigContext = createContext<ApiConfigContextValue | undefined>(undefined)

export const ApiConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<ApiConfig>(() => apiConfigStrategy.loadConfig())
  const [currentUrls, setCurrentUrls] = useState(() => apiConfigStrategy.getUrls())

  useEffect(() => {
    const loaded = apiConfigStrategy.loadConfig()
    setConfig(loaded)
    setCurrentUrls(apiConfigStrategy.getUrls())
  }, [])

  const updateConfig = useCallback(
    (newConfig: Partial<ApiConfig>) => {
      const updated = { ...config, ...newConfig }
      apiConfigStrategy.saveConfig(updated)
      setConfig(updated)
      setCurrentUrls(apiConfigStrategy.getUrls())
    },
    [config],
  )

  const handleReset = useCallback(() => {
    apiConfigStrategy.resetConfig()
    const defaultConfig = apiConfigStrategy.loadConfig()
    setConfig(defaultConfig)
    setCurrentUrls(apiConfigStrategy.getUrls())
  }, [])

  const value: ApiConfigContextValue = {
    config,
    updateConfig,
    resetConfig: handleReset,
    isEnabled: isApiConfigEnabled,
    appEnv: APP_ENV,
    currentUrls,
  }

  return <ApiConfigContext.Provider value={value}>{children}</ApiConfigContext.Provider>
}

export function useApiConfig(): ApiConfigContextValue {
  const context = useContext(ApiConfigContext)
  if (!context) {
    throw new Error('useApiConfig must be used within ApiConfigProvider')
  }
  return context
}

export function useOptionalApiConfig(): ApiConfigContextValue | undefined {
  return useContext(ApiConfigContext)
}
